import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useStaggerAnimation } from "../hooks/useScrollAnimation";
import { useEffect, useRef, useState, useMemo } from "react";
import PageWrapper from "../components/layout/PageWrapper";

const SkillsTopics = () => {
  const navigate = useNavigate(); // React Router navigation hook
  // ROBUST: Create enough animation refs for maximum possible pages (8 cards, 1 per page = 8 refs max)
  const gridRef1 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef2 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef3 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef4 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef5 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef6 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef7 = useStaggerAnimation({ staggerDelay: 100 });
  const gridRef8 = useStaggerAnimation({ staggerDelay: 100 });
  
  // Dynamic ref array - scales automatically with contentSections
  const allGridRefs = [gridRef1, gridRef2, gridRef3, gridRef4, gridRef5, gridRef6, gridRef7, gridRef8];
  
  // Lusion-style scroll division state
  const [isInAnimationViewport, setIsInAnimationViewport] = useState(true);
  const [currentSection, setCurrentSection] = useState(0); // 0 = animation, 1 = cards
  const [hasAnimated, setHasAnimated] = useState(false); // Track if title animation has played
  const [showParticles, setShowParticles] = useState(false); // Control particle animation timing
  const [scrollProgress, setScrollProgress] = useState(0); // Track scroll progress for text effects
  const [isTransitioningBack, setIsTransitioningBack] = useState(false); // Track smooth back transition
  const [currentPageIndex, setCurrentPageIndex] = useState(0); // Track current page for mobile swipe
  const [touchStartX, setTouchStartX] = useState(0); // Track touch start position for swipe
  const containerRef = useRef(null);
  const animationSectionRef = useRef(null);
  const cardsSectionRef = useRef(null);
  const isScrolling = useRef(false);

  const topics = useMemo(() => [
    { title: "Kubernetes & Containerized Workloads", description: "Scaling applications using AWS EKS, microservices, and autoscaling strategies.", link: "/topics/kubernetes" },
    { title: "Managing Cloud Services at Scale", description: "Multi-cloud vs. hybrid cloud, IAM security, and cloud cost optimization techniques.", link: "/topics/cloud-management" },
    { title: "AWS API Gateway & Serverless Integrations", description: "Building and optimizing high-performance APIs using API Gateway, WebSockets, and serverless computing.", link: "/topics/serverless" },
    { title: "Scalable & Resilient Databases", description: "Deep dive into DynamoDB, RDS, Aurora, and globally replicated databases.", link: "/topics/databases" },
    { title: "AI-Driven Cloud Automation", description: "Leveraging AI for cloud monitoring, automated scaling, and AI-assisted infrastructure management.", link: "/topics/ai-cloud-automation" },
    { title: "Real-Time Data Processing & IoT", description: "Handling large-scale IoT data streams using AWS IoT Core, MQTT, and edge computing solutions.", link: "/topics/iot" },
    { 
      title: "AWS Identity and Access Management (IAM)", 
      description: "Secure control over AWS resources with users, groups, roles, and policies for authentication and authorization.", 
      link: "/topics/iam" 
    },
    { title: "Neural Network Training", description: "Live AI model training updates using AWS IoT Core WebSockets.", link: "/topics/ai-training" }
  ], []);

  // Viewport mode system for scalable layout definitions
  const [viewportMode, setViewportMode] = useState('desktop');
  const [contentSections, setContentSections] = useState([]);
  const [totalSections, setTotalSections] = useState(2); // Animation + Cards (minimum)

  // Define viewport modes with their page boundaries and properties
  const viewportModes = {
    desktop: {
      pageHeight: 'h-screen',
      contentPadding: 'py-12',
      sectionSpacing: 'mb-8',
      titleSize: 'text-4xl',
      titleSpacing: 'mb-4',
      descSize: 'text-lg',
      descSpacing: 'mb-8',
      cardHeight: 'h-40',
      cardTitleSize: 'text-sm',
      cardDescSize: 'text-xs',
      cardIconSize: 'w-3 h-3',
      gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      scrollIndicatorBottom: 'bottom-8',
      scrollIndicatorLeft: 'calc(50% - var(--sidebar-width-collapsed) - var(--sidebar-width-collapsed))'
    },
    mobileLandscape: {
      pageHeight: 'min-h-screen',
      contentPadding: 'py-6',
      sectionSpacing: 'mb-4',
      titleSize: 'text-xl',
      titleSpacing: 'mb-1',
      descSize: 'text-xs italic',
      descSpacing: 'mb-4',
      cardHeight: 'h-28',
      cardTitleSize: 'text-[10px]',
      cardDescSize: 'text-[8px]',
      cardIconSize: 'w-2 h-2',
      gridCols: 'grid-cols-4',
      scrollIndicatorBottom: 'bottom-6',
      scrollIndicatorLeft: 'calc(50% - var(--sidebar-width-collapsed) - var(--sidebar-width-collapsed))'
    },
    mobilePortrait: {
      pageHeight: 'h-screen',
      contentPadding: 'py-12',
      sectionSpacing: 'mb-8',
      titleSize: 'text-4xl',
      titleSpacing: 'mb-4',
      descSize: 'text-lg',
      descSpacing: 'mb-8',
      cardHeight: 'h-32',
      cardTitleSize: 'text-xs',
      cardDescSize: 'text-[10px]',
      cardIconSize: 'w-2 h-2',
      gridCols: 'grid-cols-2',
      scrollIndicatorBottom: 'bottom-16',
      scrollIndicatorLeft: '50%'
    }
  };

  // Get current mode configuration
  const currentMode = viewportModes[viewportMode];

  // Legacy compatibility (can be removed later)
  const isMobile = viewportMode !== 'desktop';
  const isMobileLandscape = viewportMode === 'mobileLandscape';

  useEffect(() => {
    const detectViewportMode = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isLandscape = width > height;
      
      // Viewport mode detection
      let newMode;
      if (isLandscape && width <= 1024) {
        newMode = 'mobileLandscape';
      } else if (!isLandscape && width <= 768) {
        newMode = 'mobilePortrait';
      } else {
        newMode = 'desktop';
      }
      
      setViewportMode(newMode);
      
    };
    
    detectViewportMode();
    window.addEventListener('resize', detectViewportMode);
    return () => window.removeEventListener('resize', detectViewportMode);
  }, []);

  // Calculate pagination based on device and orientation
  useEffect(() => {
    const calculatePagination = () => {
      // Desktop OR Mobile Landscape: always keep all cards in one section (working perfectly)
      if (!isMobile || isMobileLandscape) {
        setContentSections([topics]);
        setTotalSections(2); // Animation + Cards
        return;
      }
      
      // Mobile portrait only: calculate if cards fit in viewport
      const viewportHeight = window.innerHeight;
      const headerSpace = 250; // Space for title, padding, indicators
      const availableHeight = viewportHeight - headerSpace;
      
      // Mobile card dimensions
      const cardHeight = 140; // h-32 + gap
      const cardsPerColumn = Math.floor(availableHeight / cardHeight);
      const columnsPerViewport = 2; // Always 2 columns on mobile
      const cardsPerPage = cardsPerColumn * columnsPerViewport;
      
      console.log('📱 Mobile Portrait Calculation:', {
        viewportHeight,
        availableHeight,
        cardsPerColumn,
        cardsPerPage,
        totalCards: topics.length,
        willSplit: cardsPerPage < topics.length
      });
      
      // If all 8 cards fit in one viewport, keep them together
      if (cardsPerPage >= topics.length) {
        setContentSections([topics]);
        setTotalSections(2); // Animation + Cards
        console.log('📱 All cards fit in one page');
      } else {
        // Split into multiple pages
        const pages = [];
        for (let i = 0; i < topics.length; i += cardsPerPage) {
          pages.push(topics.slice(i, i + cardsPerPage));
        }
        setContentSections(pages);
        setTotalSections(1 + pages.length); // Animation + multiple content pages
        console.log('📱 Split into pages:', pages.map((page, i) => `Page ${i+1}: ${page.length} cards`));
        console.log('📱 ROBUST: Animation refs available for', allGridRefs.length, 'pages, using', pages.length);
      }
    };

    calculatePagination();
    window.addEventListener('resize', calculatePagination);
    return () => window.removeEventListener('resize', calculatePagination);
  }, [isMobile, isMobileLandscape, topics, allGridRefs.length]);

  // JavaScript-based snap scrolling for desktop, hybrid for mobile
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      const deltaY = e.deltaY;
      const scrollingDown = deltaY > 0;
      const scrollingUp = deltaY < 0;
      
      // More responsive - shorter debounce
      if (isScrolling.current) return;
      
      // Determine target section
      let targetSection = currentSection;
      
      if (scrollingDown && currentSection < totalSections - 1) {
        targetSection = currentSection + 1; // Go to next section
      } else if (scrollingUp && currentSection > 0) {
        targetSection = currentSection - 1; // Go to previous section
      }
      
      // If section changed, perform snap scroll
      if (targetSection !== currentSection) {
        e.preventDefault(); // Prevent default when changing sections
        isScrolling.current = true;
        
        // Get target element
        let targetElement;
        if (targetSection === 0) {
          targetElement = animationSectionRef.current;
        } else {
          // For mobile portrait horizontal swipe mode, scroll to the swipe container
          if (viewportMode === 'mobilePortrait' && contentSections.length > 1) {
            const swipeContainer = container.querySelector('.w-full.h-screen.overflow-hidden');
            targetElement = swipeContainer;
            // Also update the current page to show the first page when scrolling down
            if (scrollingDown) {
              setCurrentPageIndex(0);
            }
          } else {
            // Find the corresponding content section element (vertical layout)
            const contentIndex = targetSection - 1;
            const contentElements = container.querySelectorAll('[data-content-section]');
            targetElement = contentElements[contentIndex];
          }
        }
        
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
        
        // Update state immediately
        setCurrentSection(targetSection);
        setIsInAnimationViewport(targetSection === 0);
        
        // Shorter reset time for better responsiveness
        setTimeout(() => {
          isScrolling.current = false;
        }, 400);
      } else if (!isMobile) {
        // Desktop: prevent scrolling when at boundaries
        e.preventDefault();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [currentSection, isMobile, totalSections, viewportMode, contentSections.length]);

  // Trigger animation only once on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 100); // Small delay to ensure component is mounted
    
    return () => clearTimeout(timer);
  }, []); // Empty dependency array = only runs once on mount

  // Start particle animation after text animation completes
  useEffect(() => {
    if (hasAnimated) {
      // Text animation: 18 characters × 0.1s delay + 0.6s animation duration + small buffer
      const particleDelay = (18 * 0.1 + 0.6 + 0.3) * 1000; // 2.1 seconds total
      
      const particleTimer = setTimeout(() => {
        setShowParticles(true);
      }, particleDelay);
      
      return () => clearTimeout(particleTimer);
    }
  }, [hasAnimated]);

  // Mobile portrait horizontal swipe handling
  const handleTouchStart = (e) => {
    if (viewportMode === 'mobilePortrait' && contentSections.length > 1) {
      setTouchStartX(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (viewportMode === 'mobilePortrait' && contentSections.length > 1) {
      const touchEndX = e.changedTouches[0].clientX;
      const swipeDistance = touchStartX - touchEndX;
      const minSwipeDistance = 50; // Minimum swipe distance to trigger page change

      if (Math.abs(swipeDistance) > minSwipeDistance) {
        if (swipeDistance > 0 && currentPageIndex < contentSections.length - 1) {
          // Swipe left - next page
          setCurrentPageIndex(prev => prev + 1);
          console.log('📱 Swipe: Next page', currentPageIndex + 1);
        } else if (swipeDistance < 0 && currentPageIndex > 0) {
          // Swipe right - previous page  
          setCurrentPageIndex(prev => prev - 1);
          console.log('📱 Swipe: Previous page', currentPageIndex - 1);
        }
      }
    }
  };

  // Track scroll progress for text effects - DUAL-WAY SCROLL SYSTEM
  // COMPLEXITY INSIGHT: This implements a hybrid scroll tracking system that handles both:
  // 1. FINE-GRAINED: Real-time scroll progress within section 0 (0-1 range)
  // 2. COARSE-GRAINED: Section-based changes for immediate visual feedback
  // This dual approach ensures smooth transitions during scroll AND responsive section snapping
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Skip scroll handling during smooth transition back
      if (isTransitioningBack) return;
      
      const scrollTop = container.scrollTop;
      const windowHeight = window.innerHeight;
      
      // Calculate progress within the first section (0-1)
      const progress = Math.min(scrollTop / windowHeight, 1);
      setScrollProgress(progress);
      
      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Scroll:', { scrollTop, windowHeight, progress });
      }
    };

    // SECTION-BASED SCROLL PROGRESS: Update scroll progress when sections change
    // This ensures immediate visual feedback when snapping between sections
    const updateScrollProgress = () => {
      if (currentSection === 0) {
        // When returning to animation section, check if we're transitioning back
        const scrollTop = container.scrollTop;
        const windowHeight = window.innerHeight;
        const progress = Math.min(scrollTop / windowHeight, 1);
        
        // If scroll position is at top but we were previously in another section,
        // trigger smooth transition back to full size
        if (scrollTop === 0 && scrollProgress > 0.5) {
          setIsTransitioningBack(true);
          // Gradually reduce scroll progress over time
          let currentProgress = scrollProgress;
          const transitionInterval = setInterval(() => {
            currentProgress *= 0.75; // Faster exponential decay for quicker transition
            setScrollProgress(currentProgress);
            
            if (currentProgress < 0.05) {
              setScrollProgress(0);
              setIsTransitioningBack(false);
              clearInterval(transitionInterval);
            }
          }, 30); // 30ms intervals for faster animation
        } else {
          setScrollProgress(progress);
        }
      } else {
        // Set to full fade when in content sections
        setScrollProgress(1);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    updateScrollProgress(); // Update immediately based on current section
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentSection, isTransitioningBack, scrollProgress]);

  return (
    <PageWrapper pageName="projects" enableParticles={showParticles && isInAnimationViewport}>
      <Helmet>
        <title>Projects - limkc</title>
        <meta name="description" content="AI, cloud, and automation projects core Infrastructure." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div 
        ref={containerRef} 
        className="w-full h-full overflow-auto"
      >
        {/* Full Viewport Animation Section */}
        <div 
          ref={animationSectionRef}
          className={`relative w-full flex items-center justify-center ${currentMode.pageHeight}`}
          style={{
            backgroundColor: 'transparent',
            color: 'var(--text-primary)'
          }}
        >
          <div className="text-center z-10 relative max-w-4xl mx-auto">
            <h1 
              className="text-6xl md:text-8xl font-bold mb-6 animate-title"
              style={{ 
                color: 'var(--text-primary)',
                fontSize: 'clamp(3rem, 8vw, 8rem)',
                fontWeight: '400',
                lineHeight: '100%',
                letterSpacing: '-0.02em',
                // DUAL-WAY SCROLL EFFECTS: Combines fine-grained scroll + section-based state
                // BEHAVIOR: 
                // - Section 0 + scroll progress 0: Full opacity (1.0) + full scale (1.0)
                // - Section 0 + scroll progress 1: 20% opacity (0.2) + 80% scale (0.8)  
                // - Section 1+: Locked at 20% opacity + 80% scale for consistency
                // - Returning to Section 0: Smooth exponential transition back (0.75 decay rate @ 30ms)
                opacity: currentSection === 0 ? (1 - scrollProgress * 0.8) : 0.2, // Fade to 20% opacity
                transform: currentSection === 0 ? `scale(${1 - scrollProgress * 0.2})` : 'scale(0.8)', // Shrink by 20%
                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
              }}
            >
              <style>{`
                @keyframes char-appear {
                  0% {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
                .animate-char {
                  animation: char-appear 0.6s ease-out forwards;
                  animation-fill-mode: both;
                }
              `}</style>
              <div>
                {"Beyond".split('').map((char, index) => (
                  <span 
                    key={index}
                    className={hasAnimated ? "inline-block animate-char" : "inline-block"}
                    style={{
                      animationDelay: hasAnimated ? `${index * 0.1}s` : '0s',
                      opacity: hasAnimated ? 0 : 1,
                      transform: hasAnimated ? 'translateY(20px)' : 'translateY(0)'
                    }}
                  >
                    {char}
                  </span>
                ))}
              </div>
              <div>
                {"Scalability".split('').map((char, index) => (
                  <span 
                    key={index + 6}
                    className={hasAnimated ? "inline-block animate-char" : "inline-block"}
                    style={{
                      animationDelay: hasAnimated ? `${(index + 7) * 0.1}s` : '0s',
                      opacity: hasAnimated ? 0 : 1,
                      transform: hasAnimated ? 'translateY(20px)' : 'translateY(0)'
                    }}
                  >
                    {char}
                  </span>
                ))}
              </div>
            </h1>
            <p 
              className="text-xl md:text-2xl"
              style={{ 
                color: 'var(--text-secondary)',
                fontSize: 'clamp(1rem, 2vw, 1.5rem)',
                lineHeight: '140%',
                letterSpacing: '0.05em',
                // Apply same dual-way scroll effects as the title
                opacity: currentSection === 0 ? (1 - scrollProgress * 0.8) : 0.2, // Fade to 20% opacity
                transform: currentSection === 0 ? `scale(${1 - scrollProgress * 0.2})` : 'scale(0.8)', // Shrink by 20%
                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
              }}
            >
            </p>
          </div>
          
          {/* Scroll indicator at soft edge, mode-specific positioning */}
          <div 
            className={`absolute ${currentMode.scrollIndicatorBottom} animate-bounce pointer-events-none ${
              viewportMode === 'mobilePortrait' ? 'left-0 right-0 flex justify-center' : ''
            }`}
            style={viewportMode === 'mobilePortrait' ? {
              // Apply same dual-way scroll effects
              opacity: currentSection === 0 ? (1 - scrollProgress * 0.8) : 0.2,
              transform: `scale(${currentSection === 0 ? (1 - scrollProgress * 0.2) : 0.8})`,
              transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
            } : { 
              left: currentMode.scrollIndicatorLeft, 
              transform: `translateX(-50%) scale(${currentSection === 0 ? (1 - scrollProgress * 0.2) : 0.8})`,
              width: 'max-content',
              // Apply same dual-way scroll effects
              opacity: currentSection === 0 ? (1 - scrollProgress * 0.8) : 0.2,
              transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
            }}
          >
            <div className="text-sm uppercase tracking-wider text-center" style={{ color: 'var(--text-secondary)' }}>
              Scroll to explore
            </div>
          </div>
        </div>

        {/* Dynamic Content Sections - Auto-paginated based on viewport fit */}
        {viewportMode === 'mobilePortrait' && contentSections.length > 1 ? (
          // MOBILE PORTRAIT WITH MULTIPLE PAGES: Horizontal swipe layout
          <div 
            className="w-full h-screen overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="flex h-full transition-transform duration-300 ease-out"
              style={{
                width: `${contentSections.length * 100}%`,
                transform: `translateX(-${currentPageIndex * (100 / contentSections.length)}%)`
              }}
            >
              {contentSections.map((sectionTopics, sectionIndex) => (
                <div 
                  key={sectionIndex}
                  data-content-section={sectionIndex}
                  className={`flex-shrink-0 flex justify-center px-4 transition-all duration-300 ${currentMode.contentPadding} items-center`}
                  style={{
                    width: `${100 / contentSections.length}%`,
                    height: '100%',
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)'
                  }}
                >
                  <div className="max-w-7xl mx-auto w-full">
                    {sectionIndex === 0 && (
                      <div className={`text-center ${currentMode.sectionSpacing}`}>
                        <h2 className={`font-bold ${currentMode.titleSize} ${currentMode.titleSpacing}`} style={{ color: 'var(--text-primary)' }}>Explore Topics</h2>
                        <p className={`${currentMode.descSize} ${currentMode.descSpacing}`} style={{ color: 'var(--text-secondary)' }}>
                          Deep dive into cloud architecture, AI automation, and scalable infrastructure
                        </p>
                      </div>
                    )}
                    
                    <div 
                      ref={allGridRefs[sectionIndex] || null}
                      className={`grid gap-4 ${currentMode.gridCols}`}
                    >
                      {sectionTopics.map((topic, topicIndex) => (
                        <div 
                          key={`${sectionIndex}-${topicIndex}`} 
                          data-animate
                          className="animate-fade-in-sequential"
                        >
                          <div
                            onClick={() => navigate(topic.link)} 
                            className={`p-3 shadow-md rounded-lg cursor-pointer border apple-smooth-hover transition-all duration-300 hover:transform hover:scale-105 ${currentMode.cardHeight} flex flex-col justify-between`}
                            style={{
                              backgroundColor: 'var(--bg-secondary)',
                              borderColor: 'var(--text-secondary)',
                              borderWidth: '1px'
                            }}
                          >
                            <div>
                              <h3 className={`${currentMode.cardTitleSize} font-semibold mb-1 leading-tight`} style={{ color: 'var(--text-primary)' }}>{topic.title}</h3>
                              <p className={`${currentMode.cardDescSize} line-clamp-2`} style={{ color: 'var(--text-secondary)' }}>{topic.description}</p>
                            </div>
                            
                            <div className={`flex items-center ${currentMode.cardDescSize} mt-1`} style={{ color: 'var(--text-secondary)' }}>
                              <span className="mr-1">Explore</span>
                              <svg className={`${currentMode.cardIconSize} transform transition-transform group-hover:translate-x-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Page indicators for swipe navigation */}
                    <div className="mt-8 text-center">
                      <div className="flex justify-center space-x-2">
                        {contentSections.map((_, index) => (
                          <div 
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              currentPageIndex === index ? 'bg-blue-500' : 'bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                        {currentPageIndex + 1} of {contentSections.length}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // DESKTOP & MOBILE LANDSCAPE & SINGLE PAGE: Vertical scroll sections
          contentSections.map((sectionTopics, sectionIndex) => (
          <div 
            key={sectionIndex}
            data-content-section={sectionIndex}
            ref={sectionIndex === 0 ? cardsSectionRef : null}
            className={`w-full flex justify-center px-4 transition-all duration-300 ${currentMode.pageHeight} ${currentMode.contentPadding} items-center`}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)'
            }}
          >
            <div className="max-w-7xl mx-auto w-full">
              {sectionIndex === 0 && (
                <div className={`text-center ${currentMode.sectionSpacing}`}>
                  <h2 className={`font-bold ${currentMode.titleSize} ${currentMode.titleSpacing}`} style={{ color: 'var(--text-primary)' }}>Explore Topics</h2>
                  <p className={`${currentMode.descSize} ${currentMode.descSpacing}`} style={{ color: 'var(--text-secondary)' }}>
                    Deep dive into cloud architecture, AI automation, and scalable infrastructure
                  </p>
                </div>
              )}
              
              {/* Grid Layout - Mode-based responsive columns */}
              <div 
                ref={allGridRefs[sectionIndex] || null}
                className={`grid gap-4 ${currentMode.gridCols}`}
              >
                {sectionTopics.map((topic, topicIndex) => (
                  <div 
                    key={`${sectionIndex}-${topicIndex}`} 
                    data-animate
                    className="animate-fade-in-sequential"
                  >
                    <div
                      onClick={() => navigate(topic.link)} 
                      className={`p-3 shadow-md rounded-lg cursor-pointer border apple-smooth-hover transition-all duration-300 hover:transform hover:scale-105 ${currentMode.cardHeight} flex flex-col justify-between`}
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        borderColor: 'var(--text-secondary)',
                        borderWidth: '1px'
                      }}
                    >
                      <div>
                        <h3 className={`${currentMode.cardTitleSize} font-semibold mb-1 leading-tight`} style={{ color: 'var(--text-primary)' }}>{topic.title}</h3>
                        <p className={`${currentMode.cardDescSize} line-clamp-2`} style={{ color: 'var(--text-secondary)' }}>{topic.description}</p>
                      </div>
                      
                      {/* Arrow indicator */}
                      <div className={`flex items-center ${currentMode.cardDescSize} mt-1`} style={{ color: 'var(--text-secondary)' }}>
                        <span className="mr-1">Explore</span>
                        <svg className={`${currentMode.cardIconSize} transform transition-transform group-hover:translate-x-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Section indicator */}
              {contentSections.length > 1 && (
                <div className="mt-8 text-center">
                  <div className="flex justify-center space-x-2">
                    {contentSections.map((_, index) => (
                      <div 
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          currentSection === index + 1 ? 'bg-blue-500' : 'bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                    Page {sectionIndex + 1} of {contentSections.length}
                  </p>
                </div>
              )}
            </div>
            
            {/* Scroll indicator for THIS soft page - only if not the last section */}
            {sectionIndex < contentSections.length - 1 && (
              <div 
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce pointer-events-none"
              >
                <div className="text-sm uppercase tracking-wider text-center" style={{ color: 'var(--text-secondary)' }}>
                  More content
                </div>
              </div>
            )}
          </div>
        ))
        )}
      </div>
      
    </PageWrapper>
  );
};

export default SkillsTopics;
